import { persistentAtom } from '@nanostores/persistent';
import { action, computed, atom } from 'nanostores';
import Decimal from 'decimal.js';
import { patientWalletBalance } from './patient';
import client from '../graphql/client';

import { PurchaseFromStorefrontDocument } from '../graphql/mutations/purchaseFromStorefront.generated';

export type CartItem = {
  cartItem: any;
  type: string;
};

export const cart = persistentAtom<CartItem[]>('cart', [], {
  encode: JSON.stringify,
  decode: JSON.parse,
});

export const purchaseInProgress = atom<boolean>(false);
export const purchaseError = atom<string | null>(null);
export const invoiceConfirmationId = atom<string | null>(null);
export const useWalletBalance = atom<boolean>(false);
export const testingAmountOverride = atom<number | null>(null);
export const shippingMethod = atom<string>('');

export const setShippingMethod = action(
  shippingMethod,
  'setShippingMethod',
  (store, method) => {
    store.set(method);
  }
);

export const setTestingAmountOverride = (value) => {
  console.log('setting testing amount override', value);
  testingAmountOverride.set(value);
};

export const setUseWalletBalance = action(
  useWalletBalance,
  'useWalletBalance',
  (store, value) => {
    store.set(value);
  }
);

export async function purchase(data) {
  const currentCart = [...computedCart.get()];

  const { ...rest } = data;
  const input = {
    ...rest,
    ...(useWalletBalance.get() && {
      walletAmountToUse: ledger.get().walletUsedNumber,
    }),
    workspaceId: data.workspaceId,
    memberships: currentCart
      .filter((item) => item.type === 'membership')
      .map((item) => ({
        membershipId: item.cartItem.id,

        taxRate: item.cartItem.taxRate,

        name: item.cartItem.name,
        pricePerUnit: item.cartItem.price,
        quantity: 1,
        allowGratuity: false,
        discountAmount: 0,
        discountType: 'fixed',

        type: 'membership',
        useCount: 0,
      })),
    packages: currentCart
      .filter((item) => item.type === 'package')
      .map((item) => ({
        packageId: item.cartItem.id,

        taxRate: item.cartItem.taxRate,
        useCount: 0,
        quantity: 1,

        name: item.cartItem.name,
        pricePerUnit: item.cartItem.price,

        allowGratuity: false,
        discountAmount: 0,
        discountType: 'fixed',

        type: 'package',
      })),
    promotions: currentCart
      .filter((item) => item.type === 'promotion')
      .map((item) => ({
        promotionId: item.cartItem.id,

        quantity: item.quantity,
        name: item.cartItem.name,

        pricePerUnit: item.cartItem.price,

        allowGratuity: false,
        discountAmount: 0,
        discountType: 'fixed',
        taxRate: 0,
        type: 'promotion',
        useCount: 0,
      })),
    services: currentCart
      .filter((item) => item.type === 'product')
      .map((item) => ({
        serviceId: item.cartItem.id,

        taxRate: item.cartItem.taxRate,
        pricePerUnit: item.cartItem.price,
        quantity: item.quantity,

        name: item.cartItem.name,

        allowGratuity: false,
        discountAmount: 0,
        discountType: 'fixed',

        type: 'product',
        useCount: 0,
      })),
    lineItems: currentCart
      .filter((item) => item.type === 'shipping')
      .map((item) => ({
        name: item.cartItem.name,
        pricePerUnit: item.cartItem.price,
        quantity: 1,
        allowGratuity: false,
        discountAmount: 0,
        discountType: 'fixed',
        taxRate: 0,
        type: 'other',
        useCount: 0,
      })),
  };
  purchaseError.set(null);
  purchaseInProgress.set(true);

  try {
    const purchaseResult = await client.mutate({
      mutation: PurchaseFromStorefrontDocument,
      variables: {
        input,
      },
    });

    invoiceConfirmationId.set(
      purchaseResult.data.purchaseFromStorefront.invoiceId
    );
  } catch (err) {
    purchaseError.set(err.message);
  }

  purchaseInProgress.set(false);

  try {
    (window as any).DD_RUM.setUser({
      email: data.email,
    });
  } catch (err) {}
}

export function addToCart(cartItem, type) {
  let currentCart = [...cart.get()];

  if (
    type === 'shipping' &&
    currentCart.some(
      (item) => item.cartItem.id === cartItem.id && item.type === type
    )
  ) {
    currentCart = currentCart.map((item) =>
      item.type === 'shipping' ? { cartItem, type } : item
    );
    cart.set(currentCart);
    return;
  }
  // Check if the type is 'membership' or 'package' and if it already exists in the cart
  if (
    (type === 'membership' || type === 'package') &&
    currentCart.some(
      (item) => item.cartItem.id === cartItem.id && item.type === type
    )
  ) {
    return;
  }

  currentCart.push({ cartItem, type });
  cart.set(currentCart);
}

export const removeFromCart = action(
  cart,
  'removeFromCart',
  (store, cartItem) => {
    const currentCart = [...store.get()];
    const existingItemIndex = currentCart.findIndex(
      (item) => item.cartItem.id === cartItem.cartItem.id
    );

    if (existingItemIndex > -1) {
      // Remove the item from the cart
      const newCart = [
        ...currentCart.slice(0, existingItemIndex),
        ...currentCart.slice(existingItemIndex + 1),
      ];
      const hasShippableItems = newCart.some(
        (item) => item.cartItem.isShippable
      );
      if (!hasShippableItems) {
        const noShippingCart = newCart.filter(
          (item) => item.type !== 'shipping'
        );
        store.set(noShippingCart);
      } else {
        store.set(newCart);
      }
    }

    return store.get();
  }
);

export function quantityOfItemIncart(cartItem) {
  const items = cart.get().filter((item) => item.cartItem.id === cartItem.id);
  return items.length;
}

export const ledger = computed([cart, useWalletBalance], () => {
  let total = new Decimal(0);
  let totalTax = new Decimal(0);
  let walletBalance = new Decimal(patientWalletBalance.get() ?? 0);
  const cartItems = cart.get();
  cartItems.forEach((item) => {
    const price = new Decimal(item.cartItem.price);
    const taxRate = new Decimal(item.cartItem.taxRate ?? 0);
    const tax = price.mul(taxRate);

    total = total.plus(price);
    totalTax = totalTax.plus(tax);
  });

  let grandTotal = total.plus(totalTax);
  let walletUsed = new Decimal(0);

  if (useWalletBalance.get()) {
    if (walletBalance.lessThan(grandTotal)) {
      walletUsed = walletBalance;
      grandTotal = grandTotal.minus(walletBalance);
      walletBalance = new Decimal(0);
    } else {
      walletUsed = grandTotal;
      walletBalance = walletBalance.minus(grandTotal);
      grandTotal = new Decimal(0);
    }
  }

  return {
    total: total.div(100).toFixed(2).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    totalTax: totalTax.div(100).toFixed(2).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    grandTotal: grandTotal.div(100).toFixed(2).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    grandTotalNumber: grandTotal.div(100).toNumber(),
    walletUsed: walletUsed.div(100).toFixed(2).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    walletUsedNumber: walletUsed.round().toNumber(),
    walletBalance: walletBalance.div(100).toFixed(2).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
  };
});

export function countCartItem(cartItem) {
  const items = cart.get().filter((item) => item.cartItem.id === cartItem.id);
  return items.length;
}

export const computedCart = computed([cart], (cart) => {
  const groupedCart = cart.reduce((acc, curr) => {
    const existingItem = acc.find(
      (item) =>
        item.cartItem.id === curr.cartItem.id &&
        ['product', 'promotion'].includes(item.type)
    );
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      // For package or membership, don't count quantity over 1, instead make it a new line item in the cart
      if (curr.type === 'product' || curr.type === 'promotion') {
        acc.push({ ...curr, quantity: 1 });
      } else if (['package', 'membership', 'shipping'].includes(curr.type)) {
        const existingPackageOrMembership = acc.find(
          (item) =>
            item.cartItem.id === curr.cartItem.id &&
            ['package', 'membership', 'shipping'].includes(item.type)
        );
        if (!existingPackageOrMembership) {
          acc.push({ ...curr, quantity: 1 });
        }
      }
    }
    return acc;
  }, []);
  return groupedCart;
});
